import React from 'react';
import { Image, Upload, Trash2, Lock, Unlock } from 'lucide-react';
import type { ImageConfigProps } from './types';
import { calculateLogoDimensions, getLogoDimensionCapsForMode } from './useLogoUpload';
import { NumberInput } from '../ComponentEditModal/shared/NumberInput';

export const ImageConfiguration: React.FC<ImageConfigProps> = ({
  logoData,
  onChange,
  onFileSelect,
  onClearImage,
  isUploading,
  hasImage,
  fileInputRef,
}) => {
  const lockAspectRatio = logoData.lockAspectRatio !== false; // Default to true
  const caps = getLogoDimensionCapsForMode('image');

  // Handle height change - recalculate width if aspect ratio is locked
  const handleHeightChange = (newHeight: number) => {
    const clampedHeight = Math.min(Math.max(newHeight, caps.minHeight), caps.maxHeight);

    if (lockAspectRatio && logoData.originalWidth && logoData.originalHeight) {
      const dimensions = calculateLogoDimensions(
        logoData.originalWidth,
        logoData.originalHeight,
        clampedHeight,
        true,
        caps
      );
      onChange({ imageHeight: dimensions.height, imageWidth: dimensions.width });
    } else {
      onChange({ imageHeight: clampedHeight });
    }
  };

  // Handle width change (only when unlocked)
  const handleWidthChange = (newWidth: number) => {
    const clampedWidth = Math.min(Math.max(newWidth, caps.minWidth), caps.maxWidth);
    onChange({ imageWidth: clampedWidth });
  };

  // Toggle aspect ratio lock
  const handleToggleLock = () => {
    const newLockState = !lockAspectRatio;
    onChange({ lockAspectRatio: newLockState });

    // If turning lock ON and we have original dimensions, recalculate
    if (newLockState && logoData.originalWidth && logoData.originalHeight) {
      const dimensions = calculateLogoDimensions(
        logoData.originalWidth,
        logoData.originalHeight,
        logoData.imageHeight || 48,
        true,
        caps
      );
      onChange({
        lockAspectRatio: newLockState,
        imageHeight: dimensions.height,
        imageWidth: dimensions.width,
      });
    }
  };

  // Get display values
  const displayHeight = logoData.imageHeight || 48;
  const displayWidth = logoData.imageWidth || 120;

  return (
    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
      <h3 className="font-medium text-gray-900 flex items-center space-x-2">
        <Image size={18} />
        <span>Image Logo</span>
      </h3>

      {/* Upload Area */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
        {hasImage ? (
          <div className="space-y-4">
            <img
              src={logoData.cloudfrontUrl || logoData.imageUrl}
              alt="Logo preview"
              className="max-w-full max-h-32 mx-auto object-contain"
            />
            <div className="flex justify-center space-x-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                disabled={isUploading}
              >
                Replace
              </button>
              <button
                onClick={onClearImage}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                disabled={isUploading}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-blue-600 hover:text-blue-700 font-medium"
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload logo image'}
              </button>
              <p className="text-sm text-gray-500 mt-1">PNG, JPG, SVG, or WebP up to 10MB</p>
            </div>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Logo Size Controls */}
      {hasImage && (
        <div className="space-y-3">
          {/* Height Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo Height (px)</label>
            <NumberInput
              value={displayHeight}
              onChange={handleHeightChange}
              fallback={48}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              min={caps.minHeight}
              max={caps.maxHeight}
            />
          </div>

          {/* Lock Aspect Ratio Toggle */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleToggleLock}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                lockAspectRatio
                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {lockAspectRatio ? <Lock size={16} /> : <Unlock size={16} />}
              <span>{lockAspectRatio ? 'Aspect ratio locked' : 'Aspect ratio unlocked'}</span>
            </button>
          </div>

          {/* Width Display/Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Width (px){' '}
              {lockAspectRatio && (
                <span className="text-gray-400 font-normal">— auto-calculated</span>
              )}
            </label>
            {lockAspectRatio ? (
              <div className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-600">
                {displayWidth}px
              </div>
            ) : (
              <NumberInput
                value={displayWidth}
                onChange={handleWidthChange}
                fallback={120}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                min={caps.minWidth}
                max={caps.maxWidth}
              />
            )}
          </div>

          {/* Constraint Hint */}
          <p className="text-xs text-gray-500">
            Logo is sized to fit the navbar (max {caps.maxHeight}px tall, {caps.maxWidth}px wide).
          </p>
        </div>
      )}
    </div>
  );
};
